import * as Sentry from '@sentry/react'
import axios from 'axios'
import { useEffect } from 'react'

import buildInfo from '@/build-info.json'
import { useAuth } from '@/lib/provider/AuthProvider'
import { useTenant } from '@/lib/provider/TenantProvider'

const SENTRY_DSN =
  'https://f57ad312907e4bd3bed2f4d225851dd9@sentry.as5405.net/6464729'

const AXIOS_401_MESSAGE = 'Request failed with status code 401'

function isUnauthorizedHttpError(error: unknown): boolean {
  let current: unknown = error
  const seen = new Set<unknown>()
  while (current != null && !seen.has(current)) {
    seen.add(current)
    if (axios.isAxiosError(current) && current.response?.status === 401) {
      return true
    }
    if (current instanceof Error && current.message === AXIOS_401_MESSAGE) {
      return true
    }
    current =
      current instanceof Error && 'cause' in current
        ? (current as Error & { cause?: unknown }).cause
        : undefined
  }
  return false
}

export function initSentry(environment: string) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    environment,
    ...(typeof buildInfo.time === 'number'
      ? { release: String(buildInfo.time) }
      : {}),
    beforeSend(event, hint) {
      if (isUnauthorizedHttpError(hint.originalException)) {
        return null
      }
      return event
    },
  })
}

export function SentryIdentitySync() {
  const [state] = useAuth()
  const { customerId } = useTenant()

  useEffect(() => {
    const user = state.user
    const userId = user?.id

    if (userId == null) {
      Sentry.setUser(null)
      Sentry.setContext('organization', null)
      return undefined
    }

    Sentry.setUser({ id: String(userId) })

    const company = user.company
    Sentry.setContext('organization', {
      cid: company?.cid ?? null,
      customer_id: company?.customer_id ?? null,
      tenant_customer_id: customerId ?? null,
    })

    return () => {
      Sentry.setUser(null)
      Sentry.setContext('organization', null)
    }
  }, [state.user, customerId])

  return null
}
